import { InputNumber, Modal, Table } from "antd";
import React, { useEffect } from "react";

export const CommonLinkProjects = (props: {
    record: any,
    visible?: boolean,
    onClose: () => void,
    claimId?: string,
    projectsList?: any[],
    index?: number | null,
    setState?: (state: any) => void,
    handleSave?: (data: unknown) => void,
    type : 'consumables' | 'subcontractors' | 'externalWorkers',
    displayOnly: boolean
}) => {
    const [selectedRecord, setSelectedRecord] = React.useState<any>(null);
    const [validationMessage, setValidationMessage] = React.useState<string | null>(null);

    const totalPercentage = () => {
        return selectedRecord?.projects?.reduce((acc: number, project: any) => {
            if (project.key !== 'total') {
                return acc + Number(project.percentage || 0);
            }
            return acc;
        }, 0) || 0;
    };

    const totalAllocatedCost = () => {
        return selectedRecord?.projects?.reduce((acc: number, project: any) => {
            if (project.key !== 'total') {
                return acc + Number(project.allocatedCost || 0);
            }
            return acc;
        }, 0) || 0;
    };


    const saveLinkedProjects = () => {
        if (Math.abs(totalPercentage() - 100) > 0.01) {
            setValidationMessage("Total allocation must be exactly 100%. Current total: " + totalPercentage() + "%");
            return;
        }
        setValidationMessage(null);
        props.setState?.((prevState: any) => ({
            ...prevState,
            dataSource: prevState.dataSource.map((item: any, idx: number) => {
                if (idx === props.index) {
                    return {
                        ...item,
                        projects: selectedRecord?.projects || [],
                    };
                }
                return item;
            }),
            updateData: prevState.updateData.map((item: any) => {
                if (item.key === props.record.key) {
                    return {
                        ...item,
                        projects: selectedRecord?.projects || [],
                    };
                }
                return item;
            }),
        }));
        props.handleSave?.(selectedRecord);

        props.onClose();
    }


    useEffect(() => {
        if (props.record) {
            let totalGbp = 0;

            switch (props.type) {
                case 'consumables':
                    totalGbp = props.record.total || 0;
                    break;
                case 'subcontractors':
                    totalGbp = (((Number(props.record.netAmount) * Number(props.record.rndAllocationPercentage)) / 100) * Number(props.record.adjustment)) / 100 || 0;
                    break;

                case 'externalWorkers':
                    const rndTotal = (parseFloat(props.record.rndAllocationPercentage) / 100) * parseFloat(props.record.netAmount);
                    const adjustmentsPercentage = props.record.isConnected ? 100 : 65;
                    totalGbp = (adjustmentsPercentage / 100) * rndTotal;
                    break;

                default:
                    break;
            }

            const existingProjects = props.record.projects || [];
            const mergedProjects = props.projectsList!.map((project: any) => {
                const existingProject = existingProjects.find((p: any) => p.projectId === project.id);

                if (existingProject) {
                    return {
                        ...existingProject,
                        projectName: project.name, // Ensure we have the latest project name
                        percentage: totalGbp > 0 ? ((Number(existingProject.allocatedCost ?? 0) / totalGbp) * 100).toFixed(2) : "0.00",
                        allocatedCost: existingProject.allocatedCost ? existingProject.allocatedCost : 0,
                    };
                } else {
                    // New project not yet allocated to this employee
                    return {
                        projectId: project.id,
                        projectName: project.name,
                        percentage: "0.00",
                        allocatedCost: "0.00"
                    };
                }
            });

            setSelectedRecord({
                ...props.record,
                projects: mergedProjects
            });
        }
    }, [props.record]);



    return <>
        <Modal
            title={'Projects for ' + props.record?.name}
            visible={props.visible}
            onCancel={props.onClose}
            okButtonProps={{
                disabled :props.displayOnly
            }}
            onOk={saveLinkedProjects}
            destroyOnClose
        >
            {validationMessage && (
                <div style={{
                    color: 'red',
                    marginBottom: 12,
                    padding: '8px 12px',
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    borderRadius: 4
                }}>
                    {validationMessage}
                </div>
            )}

            <Table
                dataSource={selectedRecord?.projects && selectedRecord?.projects.length > 0 ? [...selectedRecord.projects, {
                    key: 'total',
                    projectName: 'Total',
                    percentage: 0,
                    allocatedCost: 0,
                }] : []}
                columns={[
                    {
                        title: 'Project Name',
                        dataIndex: 'projectName',
                        key: 'projectName',
                    },
                    {
                        title: 'Allocation (%)',
                        dataIndex: 'percentage',
                        key: 'percentage',
                        render: (text, project, index) => {
                            if (project.key === 'total') {
                                return <span>{totalPercentage()}%</span>;
                            }

                            let totalCost = 0;

                            switch (props.type) {
                                case 'consumables':
                                    totalCost = props.record.total || 0;
                                    break;
                                case 'subcontractors':
                                    totalCost = (((Number(props.record.netAmount) * Number(props.record.rndAllocationPercentage)) / 100) * Number(props.record.adjustment)) / 100 || 0;
                                    break;
                                case 'externalWorkers':
                                    const rndTotal = (parseFloat(props.record.rndAllocationPercentage) / 100) * parseFloat(props.record.netAmount);
                                    const adjustmentsPercentage = props.record.isConnected ? 100 : 65;
                                    totalCost = (adjustmentsPercentage / 100) * rndTotal;
                                    break;
                                default:
                                    break;
                            }

                            return (
                                <InputNumber
                                    disabled={props.displayOnly}
                                    min={0}
                                    max={100}
                                    formatter={(value) => `${value}%`}
                                    parser={(value) => value ? value.replace('%', '') : '0'}
                                    value={project.percentage || 0}
                                    onChange={(value) => {
                                        const numValue = Number(value) || 0;

                                        const newAllocatedCost = totalCost * (numValue / 100);

                                        const updatedRecord = { ...selectedRecord };
                                        const updatedProjects = [...updatedRecord.projects];

                                        updatedProjects[index] = {
                                            ...updatedProjects[index],
                                            percentage: numValue,
                                            allocatedCost: newAllocatedCost.toFixed(2)
                                        };

                                        updatedRecord.projects = updatedProjects;

                                        setSelectedRecord(updatedRecord);
                                    }}
                                />
                            );
                        }
                    },
                    {
                        title: 'Value (GBP)',
                        dataIndex: 'allocatedCost',
                        key: 'allocatedCost',
                        render: (_, project) => {
                            const calculatedValue = project.allocatedCost ?
                                project.allocatedCost : "0.00";


                            return (
                                project.key === 'total' ?
                                    `£${totalAllocatedCost().toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : (
                                        <span>
                                            {`£${calculatedValue}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        </span>
                                    )
                            );
                        }
                    }
                ]}
                pagination={false}
                bordered
                size="small"
                locale={{ emptyText: 'No projects assigned' }}
                rowClassName={(record) => record.key === 'total' ? 'total-row' : ''}
            />
        </Modal>
    </>
};
